import { Box, Text, Tooltip, useTheme } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Exercise } from "../../models/Exercise";
import { StudentTableHomework } from "../../models/StudentTableItem";

type Props = {
  exam: Exercise;
  homeworks: StudentTableHomework[];
};

export default function ExerciseProgressItem({ exam, homeworks }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const homework = useMemo(
    () => homeworks.find((hw) => hw.examId === exam.id),
    [exam.id, homeworks],
  );

  const [bg, borderColor, tooltip] = useMemo(() => {
    if (!homework)
      return ["white", "gray.300", t("learning_path_progresses_page.todo")];

    switch (homework.status) {
      case "STARTED":
        return [
          "white",
          theme.colors.primary.DEFAULT,
          t("learning_path_progresses_page.started"),
        ];
      case "DONE":
        return [
          `linear-gradient(white 50%, ${theme.colors.primary[200]} 50% )`,
          theme.colors.primary.DEFAULT,
          t("learning_path_progresses_page.done"),
        ];
      case "REJECTED":
        return [
          theme.colors.red[500],
          theme.colors.red[200],
          t("learning_path_progresses_page.rejected"),
        ];
      case "APPROVED":
        return [
          theme.colors.green[100],
          theme.colors.green[500],
          t("learning_path_progresses_page.approved"),
        ];
      default:
        return ["white", "gray.300", t("learning_path_progresses_page.todo")];
    }
  }, [homework, t, theme.colors.green, theme.colors.primary, theme.colors.red]);

  const onClick = useCallback(() => {
    navigate(`./homeworks/${homework?.id}?exerciseId=${homework?.examId}`);
  }, [homework?.examId, homework?.id, navigate]);

  return (
    <Tooltip label={tooltip}>
      <Box
        cursor={homework ? "pointer" : "default"}
        aspectRatio={1}
        width={9}
        display="flex"
        justifyContent="center"
        alignItems="center"
        bg={bg}
        borderColor={borderColor}
        borderWidth={2}
        borderRadius={100}
        onClick={homework && onClick}
      >
        <Text as="span" fontSize="md" fontWeight={500}>
          {exam.position + 1}
        </Text>
      </Box>
    </Tooltip>
  );
}
